<template>
  <div>
    <div class="col-12 p-0 mb-1">
      <div
        class="d-flex justify-content-between flex-nowrap"
        v-if="event.google_meet_join_url"
      >
        <div class="text-truncate">
          <span class="">{{ event.google_meet_join_url }}</span>
        </div>
        <button
          type="button"
          class="btn p-0 ml-3"
          v-clipboard:copy="event.google_meet_join_url"
          v-clipboard:success="googleMeetJoinUrlCopySuccess"
          v-clipboard:error="googleMeetJoinUrlCopyError"
        >
          <i class="far fa-link"></i>
        </button>
      </div>
    </div>
    <div class="col-12 text-center p-0">
      <div class="d-flex flex-column">
        <div
          v-tooltip="{
            content: isInThePast
              ? $t('EVENTS.TEXT.PAST_EVENTS_CAN_NOT_BE_STARTED')
              : '',
            delay: 100,
            placement: 'top',
            container: 'body',
          }"
        >
          <HMButton
            color="google-meet text-nowrap w-100"
            :disabled="!canStartGoogleMeetSession || isInThePast"
            @click="startGoogleMeetSession"
            :loading="startingGoogleMeetSession"
          >
            <div
              class="d-flex justify-content-between align-items-center w-100"
            >
              <span v-if="!isStreaming" class="d-flex flex-grow-1">
                {{ getGoogleMeetStartText() }}
              </span>
              <span v-if="isStreaming" class="d-flex flex-grow-1">
                {{ $t('EVENTS.BUTTON.JOIN_GOOGLE_MEET_SESSION') }}
              </span>
              <span
                class="button-image-container d-flex flex-grow-0 justify-content-center"
              >
                <img
                  class="img-fluid img-restrict google-meet"
                  src="/assets/images/integrations/google-meet.svg"
                />
              </span>
            </div>
          </HMButton>
        </div>
        <HMButton
          v-if="isStreaming"
          color="google-meet end text-nowrap w-100 mt-3"
          @click="stopGoogleMeetSession"
          :loading="stoppingGoogleMeetSession"
        >
          <div class="d-flex justify-content-between align-items-center w-100">
            <span class="d-flex flex-grow-1">
              {{ $t('EVENTS.BUTTON.STOP_GOOGLE_MEET_SESSION') }}
            </span>
            <span class="button-image-container d-flex flex-grow-0">
              <img
                class="img-fluid img-restrict google-meet"
                src="/assets/images/integrations/google-meet.svg"
              />
            </span>
          </div>
        </HMButton>
        <div class="text-left" v-if="!isGoogleMeetConnected">
          <small
            class="text-muted mt-2"
            v-html="sanitize($t('EVENTS.TEXT.ENABLE_GOOGLE_MEET_MSG'))"
          >
          </small>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import { errorToMessage } from '../../../core/utils/errors';
import * as EventUtils from '../../../core/utils/events';
import { mapGetters } from 'vuex';
import { sanitize } from '@/core/utils/utils';
import { EVENT_TYPE_MEETING } from '../../../core/utils/events';

export default {
  name: 'EventGoogleMeetStreamingComponent',
  props: {
    event: {
      type: Object,
      default: () => {
        return {};
      },
    },
  },
  computed: {
    ...mapGetters({
      isGoogleMeetConnected: 'user/isGoogleMeetConnected',
      googleMeetInfo: 'user/googleMeetInfo',
    }),
    isSoon() {
      const now = moment();
      const eventStartTime = moment(this.event.event_start_datetime);
      return now.add(1, 'h').isAfter(eventStartTime);
    },
    isInThePast() {
      const now = moment();
      const eventDate = moment(this.event.event_end_datetime).add(1, 'day');
      return now.isAfter(eventDate);
    },
    canStartGoogleMeetSession() {
      return this.isGoogleMeetConnected;
    },
    isStreaming() {
      return this.event.google_meet_join_url;
    },
    isMeetingEvent() {
      return this.event.event_type === EVENT_TYPE_MEETING;
    },
  },
  data() {
    return {
      startingGoogleMeetSession: false,
      stoppingGoogleMeetSession: false,
    };
  },
  methods: {
    sanitize,
    getGoogleMeetStartText() {
      const key = EventUtils.getGoogleMeetEventTypeLabel(
        this.event.event_type,
        this.isSoon
      );
      if (!key) return '';
      return this.$t(key);
    },
    googleMeetJoinUrlCopySuccess() {
      this.$notify({
        group: 'app',
        type: 'success',
        title: this.$t('NOTIFICATIONS.TITLE.SUCCESS'),
        text: this.$t(
          'NOTIFICATIONS.TEXT.GOOGLE_MEET_JOIN_LINK_COPIED_TO_CLIPBOARD'
        ),
      });
    },
    googleMeetJoinUrlCopyError() {
      this.$notify({
        group: 'app',
        type: 'error',
        title: this.$t('NOTIFICATIONS.TITLE.ERROR'),
        text: this.$t('NOTIFICATIONS.TEXT.GOOGLE_MEET_JOIN_LINK_COPY_ERROR'),
        duration: -1,
      });
    },
    async startGoogleMeetSession() {
      if (this.startingGoogleMeetSession) return;
      if (!this.isSoon) {
        await this.scheduleGoogleMeetSession();
        return;
      }
      const windowReference = window.open();
      this.startingGoogleMeetSession = true;
      try {
        if (this.isStreaming) {
          windowReference.location = this.event.google_meet_join_url;
          return;
        }
        await this.$store.dispatch(
          'event/createGoogleMeetMeeting',
          this.event.id
        );
        windowReference.location = this.event.google_meet_join_url;
      } catch (error) {
        windowReference.close();
        this.$notify({
          group: 'app',
          type: 'error',
          title: 'Failed to start Google Meet Session',
          text: errorToMessage(error.response, `Request failed: ${error}`),
          duration: -1,
        });
      } finally {
        this.startingGoogleMeetSession = false;
      }
    },
    async scheduleGoogleMeetSession() {
      if (this.startingGoogleMeetSession) return;
      this.startingGoogleMeetSession = true;
      try {
        await this.$store.dispatch(
          'event/scheduleGoogleMeetMeeting',
          this.event.id
        );
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          title: 'Failed to schedule Google Meet Session',
          text: errorToMessage(error.response, `Request failed: ${error}`),
          duration: -1,
        });
      } finally {
        this.startingGoogleMeetSession = false;
      }
    },
    async stopGoogleMeetSession() {
      try {
        this.stoppingGoogleMeetSession = true;
        await this.$store.dispatch('event/endGoogleMeetMeeting', this.event.id);
        this.$notify({
          group: 'app',
          type: 'success',
          title: this.$t('NOTIFICATIONS.TITLE.SUCCESS'),
          text: this.$t(
            'NOTIFICATIONS.TEXT.GOOGLE_MEET_SESSION_SUCCESSFULLY_STOPPED'
          ),
        });
      } finally {
        this.stoppingGoogleMeetSession = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/web/styles/components-variables.scss';

.button-image-container {
  background-color: $white;
  border: 1px solid $medium-gray;
  border-left-color: transparent;
  border-top-right-radius: 6px;
  border-bottom-right-radius: 6px;
  min-height: 41px;
  margin: -9px -14px;
  padding: 13px 20px;
  min-width: 110px;

  .img-restrict {
    max-width: 68px;
    max-height: 24px;
    &.google-meet {
      max-height: 15px;
    }
  }
}
</style>
